<template>
  <div class="page-wrap">
    <PageHeader
      title="무료체험신청 관리"
      subtitle="무료체험 학교의 계정을 발급하거나 신청을 처리합니다."
      :breadcrumb="[{ label: 'GUEST 계정관리' }, { label: '무료체험신청 관리' }]"
    />

    <div
      v-if="loading === 'true'"
      class="skeleton-wrap"
    >
      <div class="gradiant-walker"></div>
      <div class="skeleton"></div>
    </div>

    <FormCard v-else-if="loading === 'error'">
      <EmptyState
        title="정보를 불러오는 중 오류가 발생했습니다"
        description="페이지 새로고침을 해주세요."
        icon="mdi-alert-circle-outline"
      >
        <template #action>
          <v-btn @click="onClickRefresh" variant="flat" color="primary" size="small">
            새로고침
          </v-btn>
        </template>
      </EmptyState>
    </FormCard>

    <FormCard v-else-if="loading === 'empty'">
      <EmptyState
        title="처리할 무료 체험신청 건이 없습니다"
        description="새로 신청된 건이 들어오면 이 목록에 표시됩니다."
        icon="mdi-inbox-outline"
      />
    </FormCard>

    <template v-else>
      <!-- 데스크탑/태블릿 ≥640px: 테이블 -->
      <DataTable
        v-if="!isMobile"
        :headers="tableHeaders"
        :items="tableItems"
        :items-per-page="15"
        row-key="idx"
        density="comfortable"
      >
        <template #toolbar>
          <span class="table-count">
            미처리 <b>{{ appLength }}</b>건
            <span class="table-meta">
              (2024.01.01 ~ {{ dayjs(new Date()).format("YYYY.MM.DD") }} 기준)
            </span>
          </span>
          <div class="toolbar-spacer" />
          <v-btn
            @click="onClickAllIssuance"
            variant="flat"
            color="primary"
            size="small"
          >
            무료체험 일괄 발급
          </v-btn>
        </template>

        <template #item.tel="{ item }">
          <span class="mono-code">{{ checkTelFormat(item.tel) }}</span>
        </template>

        <template #item.mail="{ item }">
          <span class="mono-code">{{ item.mail }}</span>
        </template>

        <template #item.issue="{ item }">
          <v-btn
            :id="item.idx"
            @click="onClickIssuance($event)"
            variant="outlined"
            color="primary"
            size="x-small"
          >
            OK
          </v-btn>
        </template>

        <template #item.delete="{ item }">
          <v-btn
            :id="item.idx"
            @click="onClickDelete($event)"
            variant="text"
            color="error"
            size="x-small"
          >
            삭제
          </v-btn>
        </template>

        <template #empty>
          <EmptyState
            title="처리할 신청 건이 없습니다"
            icon="mdi-inbox-outline"
          />
        </template>
      </DataTable>

      <!-- 모바일 ≤640px: 카드 리스트 -->
      <div v-else class="mobile-list">
        <div class="mobile-list__toolbar">
          <span class="table-count">
            미처리 <b>{{ appLength }}</b>건
          </span>
          <v-btn
            @click="onClickAllIssuance"
            variant="flat"
            color="primary"
            size="small"
          >
            일괄 발급
          </v-btn>
        </div>

        <article
          v-for="item in pagedItems"
          :key="item.idx"
          class="app-card"
        >
          <header class="app-card__head">
            <div class="app-card__avatar" aria-hidden="true">
              <v-icon icon="mdi-school" size="20" />
            </div>
            <div class="app-card__heading">
              <h3 class="app-card__name">{{ item.schoolName }}</h3>
              <p class="app-card__meta">
                <v-icon icon="mdi-calendar-clock" size="12" />
                <span>신청 {{ item.subscriptionDate }}</span>
              </p>
            </div>
          </header>

          <div class="app-card__contact">
            <div class="app-card__contact-item">
              <v-icon icon="mdi-account-tie-outline" size="14" class="app-card__contact-icon" />
              <span>{{ item.teacher || '-' }}</span>
            </div>
            <div class="app-card__contact-item">
              <v-icon icon="mdi-phone-outline" size="14" class="app-card__contact-icon" />
              <span class="mono">{{ checkTelFormat(item.tel) || '-' }}</span>
            </div>
            <div class="app-card__contact-item app-card__contact-item--full">
              <v-icon icon="mdi-email-outline" size="14" class="app-card__contact-icon" />
              <span class="mono">{{ item.mail || '-' }}</span>
            </div>
          </div>

          <div class="app-card__stats">
            <div class="app-card__stat">
              <span class="app-card__stat-value">{{ item.grade || '-' }}</span>
              <span class="app-card__stat-label">학년</span>
            </div>
            <div class="app-card__stat">
              <span class="app-card__stat-value">{{ item.publisher || '-' }}</span>
              <span class="app-card__stat-label">출판사</span>
            </div>
            <div class="app-card__stat">
              <span class="app-card__stat-value tabular-nums mono">{{ item.startDate || '-' }}</span>
              <span class="app-card__stat-label">체험 시작</span>
            </div>
          </div>

          <footer class="app-card__footer">
            <button
              :id="item.idx"
              class="app-card__action app-card__action--primary"
              @click="onClickIssuance($event)"
            >
              <v-icon icon="mdi-check" size="16" />
              발급
            </button>
            <button
              :id="item.idx"
              class="app-card__action app-card__action--reject"
              @click="onClickDelete($event)"
            >
              <v-icon icon="mdi-close" size="16" />
              삭제
            </button>
          </footer>
        </article>

        <div v-if="totalPages > 1" class="mobile-pagination">
          <v-pagination
            v-model="mobilePage"
            :length="totalPages"
            :total-visible="5"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useStore } from "vuex";
import dayjs from "dayjs";
import PageHeader from "@/components/ui/PageHeader.vue";
import FormCard from "@/components/ui/FormCard.vue";
import DataTable from "@/components/ui/DataTable.vue";
import EmptyState from "@/components/ui/EmptyState.vue";
import { createallguest } from "../../../api/user-api-list";

const store = useStore();

const loading = ref("true");
const application = ref([]);
const appLength = ref(0);

const tableHeaders = [
  { title: "No", key: "no", sortable: false, align: "center", width: 60 },
  { title: "신청 일자", key: "subscriptionDate", sortable: true, align: "center" },
  { title: "학교 이름", key: "schoolName", sortable: true, align: "center" },
  { title: "교사 이름", key: "teacher", sortable: true, align: "center" },
  { title: "이메일", key: "mail", sortable: false, align: "center" },
  { title: "전화번호", key: "tel", sortable: false, align: "center" },
  { title: "학년", key: "grade", sortable: true, align: "center" },
  { title: "출판사", key: "publisher", sortable: true, align: "center" },
  { title: "체험시작 일자", key: "startDate", sortable: true, align: "center" },
  { title: "발급", key: "issue", sortable: false, align: "center", width: 90 },
  { title: "삭제", key: "delete", sortable: false, align: "center", width: 90 },
];

const tableItems = ref([]);

// 모바일 분기
const windowWidth = ref(typeof window !== "undefined" ? window.innerWidth : 1024);
const isMobile = computed(() => windowWidth.value <= 640);
const onResize = () => { windowWidth.value = window.innerWidth; };
onMounted(() => { window.addEventListener("resize", onResize); });
onBeforeUnmount(() => { window.removeEventListener("resize", onResize); });

// 모바일 페이지네이션 — 5개씩
const mobilePage = ref(1);
const mobilePageSize = 5;
const totalPages = computed(() => Math.max(1, Math.ceil(tableItems.value.length / mobilePageSize)));
const pagedItems = computed(() => {
  const start = (mobilePage.value - 1) * mobilePageSize;
  return tableItems.value.slice(start, start + mobilePageSize);
});

onMounted(async () => {
  const res = await store.dispatch("GET_SUBSCRPTION");

  if (res === 100 || res === 200 || res === 300) {
    loading.value = "error";
  } else {
    appLength.value = res.targetEmailList.length;

    if (appLength.value === 0) {
      loading.value = "empty";
    } else {
      for (let i = 0; i < appLength.value; i++) {
        let data = {
          subscriptionDate: res.targetsubscriptionDateList[i],
          idx: res.targetIdxList[i],
          mail: res.targetEmailList[i],
          grade: res.targetGradeList[i],
          tel: res.targetPhoneList[i],
          publisher: res.targetPublisherList[i],
          schoolName: res.targetSchoolList[i],
          startDate: res.targetStartList[i],
          teacher: res.targetTeacherNameList[i],
        };
        application.value.push(data);
      }
      // 최신 신청순으로 정렬 — subscriptionDate desc (문자열/날짜 모두 처리)
      const subToMillis = (s) => {
        if (!s) return 0;
        const t = dayjs(s).valueOf();
        return Number.isFinite(t) ? t : 0;
      };
      const sorted = [...application.value].sort(
        (a, b) => subToMillis(b.subscriptionDate) - subToMillis(a.subscriptionDate),
      );
      tableItems.value = sorted.map((d, idx) => ({ ...d, no: idx + 1 }));
      loading.value = "false";
    }
  }
});

const checkTelFormat = (tel) => {
  if (!tel) return "";
  const clean = tel.replace("-", "");
  if (/^02\d{7,8}$/.test(clean)) {
    return clean.replace(/(^02)(\d{3,4})(\d{4})$/, "$1-$2-$3");
  } else if (/^0\d{9}$/.test(clean)) {
    return clean.replace(/(^0\d{2})(\d{3,4})(\d{4})$/, "$1-$2-$3");
  } else if (/^01\d{9}$/.test(clean)) {
    return clean.replace(/(^01\d)(\d{4})(\d{4})$/, "$1-$2-$3");
  }
  return tel;
};

const onClickIssuance = async (e) => {
  if (confirm("발급하시겠습니까?")) {
    const num = Number(e.target.id);
    const filtered = application.value.filter((v) => v.idx === num);
    const data = {
      idx: filtered[0].idx,
      schoolName: filtered[0].schoolName,
      grade: filtered[0].grade,
      publisher: filtered[0].publisher,
      type: "교사 연수",
      startDate: filtered[0].startDate,
      email: filtered[0].mail,
      phonenum: filtered[0].tel,
      name: filtered[0].teacher,
    };

    const res = await store.dispatch("POST_CREATE_GUEST", data);

    if (res.result === true) {
      alert(
        "무료체험 계정 발급 및 전송이 완료되었습니다.\n\n메일 : 1건\n문자 : 1건"
      );
      window.location.reload(true);
    } else {
      alert("오류가 발생하였습니다.");
    }
  }
};

const onClickDelete = async (e) => {
  if (
    confirm(
      "삭제 시, 관리자 페이지 리스트에서만 삭제되며,\n무료체험 구글시트에는 그대로 남아있습니다."
    )
  ) {
    const num = Number(e.target.id);
    const res = await store.dispatch("DELETE_SUBSCRPTION", num);

    if (res.result === true) {
      alert("삭제되었습니다.");
      window.location.reload(true);
    } else {
      alert("오류가 발생하였습니다.");
    }
  }
};

const onClickAllIssuance = async () => {
  if (confirm("일괄 발급 하시겠습니까?")) {
    createallguest();
    alert(`무료체험 계정 일괄 발급 및 전송이 완료되었습니다.`);
    window.location.reload(true);
  }
};

const onClickRefresh = () => {
  window.location.reload(true);
};
</script>

<style scoped>
.page-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.table-count {
  font-size: var(--font-body-size);
  color: var(--text-secondary);
}

.table-meta {
  margin-left: var(--space-2);
  font-size: var(--font-small-size);
  color: var(--text-tertiary);
}

.toolbar-spacer {
  flex: 1;
}

.mono-code {
  font-family: var(--font-family-mono);
  font-size: var(--font-mono-size);
  color: var(--text-primary);
}

.skeleton-wrap {
  position: relative;
  width: 100%;
  max-width: 1200px;
  height: 300px;
  background: var(--bg-subtle);
  overflow: hidden;
  border-radius: var(--radius-md);
}

@keyframes loop {
  0% { left: -300px; }
  100% { left: 110%; }
}

.skeleton {
  position: relative;
  background-image: url("../../../assets/image/skeleton.png");
  background-size: cover;
  width: 100%;
  height: 300px;
  background-color: transparent;
  overflow: hidden;
}

.gradiant-walker {
  position: absolute;
  width: 300px;
  height: 100%;
  left: 0;
  background: linear-gradient(
    90deg,
    rgba(217, 217, 217, 0) 0%,
    var(--bg-muted) 50%,
    rgba(206, 206, 206, 0) 100%
  );
  animation: loop 3s infinite;
  z-index: 0;
}

/* ─── 모바일 카드 리스트 ─── */
.mobile-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.mobile-list__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.app-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  transition: box-shadow 0.15s ease;
}
.app-card:hover { box-shadow: var(--shadow-md); }

.app-card__head {
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: var(--space-3);
  align-items: center;
}
.app-card__avatar {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary-50);
  color: var(--color-primary-600);
  border-radius: var(--radius-md);
}
.app-card__heading {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.app-card__name {
  margin: 0;
  font-size: var(--font-body-strong-size);
  font-weight: 700;
  color: var(--text-primary);
  word-break: keep-all;
  line-height: 1.3;
}
.app-card__meta {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
}

/* 연락처 */
.app-card__contact {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2) var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px solid var(--border-default);
  font-size: var(--font-small-size);
  color: var(--text-secondary);
}
.app-card__contact-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.app-card__contact-item--full {
  grid-column: 1 / -1;
}
.app-card__contact-icon { color: var(--text-tertiary); flex-shrink: 0; }

/* 통계 3-up */
.app-card__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
}
.app-card__stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: var(--space-3) var(--space-2);
  background: var(--bg-surface-raised);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  text-align: center;
}
.app-card__stat-value {
  font-size: var(--font-body-strong-size);
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
  word-break: break-all;
}
.app-card__stat-label {
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  font-weight: 500;
}

/* 액션 풋터 */
.app-card__footer {
  display: flex;
  gap: var(--space-2);
  padding-top: var(--space-3);
  border-top: 1px solid var(--border-default);
}
.app-card__action {
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  border-radius: var(--radius-md);
  font-size: var(--font-small-size);
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
}
.app-card__action:active { transform: translateY(1px); }
.app-card__action--primary {
  flex: 2 1 0;
  background: var(--color-primary-600);
  color: var(--text-on-primary);
  border: none;
}
.app-card__action--primary:hover { background: var(--color-primary-700); }
.app-card__action--reject {
  background: var(--bg-surface-raised);
  color: #B42318;
  border: 1px solid #B42318;
}
.app-card__action--reject:hover { background: var(--color-error-bg); }
.mono {
  font-family: var(--font-family-mono, "JetBrains Mono", "D2Coding", monospace);
  font-size: var(--font-mono-size);
}

.mobile-pagination {
  display: flex;
  justify-content: center;
  margin-top: var(--space-3);
}
</style>
